import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFreeShippingThreshold } from "@/hooks/useFreeShipping";

export interface VendorShippingSettings {
  vendor_id: string;
  express_price: number;
  free_shipping_threshold: number | null;
  always_free: boolean;
  pickup_available: boolean;
}

export const DEFAULT_EXPRESS_PRICE = 39;
export const DEFAULT_STANDARD_PRICE = 25;

/** Per-vendor shipping settings, keyed by vendor id. */
export function useVendorShippingSettings(): Record<string, VendorShippingSettings> {
  const { data } = useQuery({
    queryKey: ["vendor-shipping-settings"],
    queryFn: async () => {
      const { data } = await (supabase.rpc as any)("get_vendor_shipping_settings");
      const map: Record<string, VendorShippingSettings> = {};
      (data || []).forEach((row: any) => {
        map[row.vendor_id] = {
          vendor_id: row.vendor_id,
          express_price: Number(row.express_price ?? DEFAULT_EXPRESS_PRICE),
          free_shipping_threshold:
            row.free_shipping_threshold === null || row.free_shipping_threshold === undefined
              ? null
              : Number(row.free_shipping_threshold),
          always_free: !!row.always_free,
          pickup_available: row.pickup_available !== false,
        };
      });
      // The all-zero uuid row holds the platform's own (vendor-less) products.
      if (map["00000000-0000-0000-0000-000000000000"]) {
        map["_platform"] = map["00000000-0000-0000-0000-000000000000"];
      }
      return map;

    },
    staleTime: 5 * 60_000,
  });
  return data ?? {};
}

/**
 * Resolves shipping for a single vendor group:
 * vendor settings win, otherwise the global defaults are used.
 */
export function resolveVendorShipping(
  settings: VendorShippingSettings | undefined,
  vendorSubtotal: number,
  globalThreshold: number,
  optionId: string
) {
  const isStandard = optionId === "standard";
  const basePrice = isStandard
    ? DEFAULT_STANDARD_PRICE
    : settings?.express_price ?? DEFAULT_EXPRESS_PRICE;
  const threshold = settings?.free_shipping_threshold ?? globalThreshold;
  const isPickup = optionId === "pickup";
  const isFree =
    isPickup || !!settings?.always_free || (isStandard && threshold > 0 && vendorSubtotal >= threshold);
  return {
    price: isFree ? 0 : basePrice,
    basePrice,
    threshold,
    isFree,
    alwaysFree: !!settings?.always_free,
    pickupAvailable: settings?.pickup_available !== false,
  };
}

/** Convenience hook for a single vendor. */
export function useVendorShipping(vendorId: string, vendorSubtotal: number, optionId: string) {
  const all = useVendorShippingSettings();
  const globalThreshold = useFreeShippingThreshold();
  return resolveVendorShipping(all[vendorId], vendorSubtotal, globalThreshold, optionId);
}
